import styles from '../styles.module.scss';
import cn from 'classnames';
import { parseISO, isValid } from 'date-fns';
import { forwardRef, useRef, useImperativeHandle } from 'react';
import Flatpickr from 'react-flatpickr';
import { FieldError } from 'react-hook-form';
import { formatDatePlain } from 'utils/formats';

export interface DateInputProps {
  error?: FieldError;
  additionalClass?: string;
  isSmallSize?: boolean;
  onDateChange?: (date: string | null) => void;
  value?: Date | null;
  onChange?: (date: string | null) => void;
  position?: 'auto' | 'above' | 'below' | 'below right' | 'below left';
  minDate?: Date | string;
  disabled?: boolean;
  maxDate?: Date | string;
  placeholder?: string;
}

export interface DateInputRef {
  clear: () => void;
  open: () => void;
  close: () => void;
}

export const DateInput = forwardRef<DateInputRef, DateInputProps>(
  (
    {
      error,
      additionalClass = '',
      isSmallSize = false,
      onDateChange,
      value,
      onChange,
      position,
      minDate,
      maxDate,
      placeholder = 'Select Date...',
      disabled = false,
      ...rest
    },
    ref
  ) => {
    const flatpickrRef = useRef<Flatpickr>(null);

    useImperativeHandle(ref, () => ({
      clear: () => {
        flatpickrRef.current?.flatpickr.clear();
      },
      open: () => {
        flatpickrRef.current?.flatpickr.open();
      },
      close: () => {
        flatpickrRef.current?.flatpickr.close();
      }
    }));

    const handleDateChange = (selectedDates: Date[]) => {
      const selectedDate = selectedDates?.[0] || null;

      if (typeof onChange === 'function') {
        onChange(selectedDate ? formatDatePlain(selectedDate) : null);
      }

      if (typeof onDateChange === 'function') {
        onDateChange(selectedDate ? formatDatePlain(selectedDate) : null);
      }
    };

    const formatValueForFlatpickr = (dateValue: Date | string | null) => {
      if (!dateValue) return undefined;
      let date: Date;

      if (typeof dateValue === 'string') {
        date = parseISO(dateValue); // Для ISO формата "2025-05-12T00:00:00"
      } else {
        date = dateValue;
      }
      return isValid(date) ? date : undefined;
    };

    return (
      <div className={cn('position-relative', styles.datePickerWrapper)}>
        <Flatpickr
          ref={flatpickrRef}
          placeholder={placeholder}
          className={cn('form-control form-control-solid pe-14', additionalClass, {
            [styles.invalidInput]: error?.message,
            'form-control-sm': isSmallSize
          })}
          value={formatValueForFlatpickr(value || null)}
          onChange={handleDateChange}
          disabled={disabled}
          options={{
            dateFormat: 'd/m/Y',
            static: !position,
            locale: {
              firstDayOfWeek: 1
            },
            position,
            minDate,
            maxDate
          }}
          {...rest}
        />
        <div className="av-calendar-icon-sm">
          <i
            className="ki-duotone ki-calendar fs-2 me-2"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              flatpickrRef.current?.flatpickr.open();
            }}
          >
            <span className="path1"></span>
            <span className="path2"></span>
          </i>
        </div>
      </div>
    );
  }
);

DateInput.displayName = 'DateInput';
